import { useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";

interface User {
  id: string;
  full_name: string | null;
  email: string | null;
  per_message_rate: number | null;
}

interface Props {
  selectedUsers: User[];
  onClose: () => void;
  onSaved: () => void;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export default function BulkRateModal({ selectedUsers, onClose, onSaved }: Props) {
  const { session } = useAuth();
  const [rateValue, setRateValue] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleSave = useCallback(async () => {
    setError(null);
    setSaved(false);
    const token = session?.access_token;
    if (!token) {
      setError("로그인이 필요합니다.");
      return;
    }

    let payloadRate: number | null = null;
    if (rateValue !== "" && rateValue !== null && rateValue !== undefined) {
      const numeric = Number(rateValue);
      if (Number.isNaN(numeric) || numeric < 0) {
        setError("유효한 금액을 입력하세요.");
        return;
      }
      payloadRate = numeric;
    }

    const userIds = selectedUsers.map((u) => u.id);
    if (userIds.length === 0) {
      setError("선택된 회원이 없습니다.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-user-rate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_ids: userIds,
          per_message_rate: payloadRate,
        }),
      });
      const resBody = await res.json().catch(() => null);
      if (!res.ok) {
        console.error("[bulkSaveRate] response error:", resBody);
        throw new Error(resBody?.error || `HTTP ${res.status}`);
      }
      setSaved(true);
      setTimeout(() => {
        onSaved();
        onClose();
      }, 1200);
    } catch (err: any) {
      console.error("[bulkSaveRate] catch error:", err);
      setError(err.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  }, [session, rateValue, selectedUsers, onSaved, onClose]);

  const displayUsers = selectedUsers.slice(0, 5);
  const remaining = selectedUsers.length - displayUsers.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#0c0c1a] shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10">
              <i className="ri-coins-line text-amber-400 text-sm"></i>
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">일괄 가격 설정</h3>
              <p className="text-gray-500 text-xs">{selectedUsers.length.toLocaleString()}명의 회원에게 적용</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Selected users preview */}
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3">
            <div className="flex flex-wrap gap-2">
              {displayUsers.map((u) => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.06] text-gray-300 text-xs"
                >
                  <i className="ri-user-line text-gray-500 text-[10px]"></i>
                  {u.full_name || u.email || "이름 없음"}
                </span>
              ))}
              {remaining > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-lg bg-white/[0.06] text-gray-500 text-xs">
                  +{remaining}명
                </span>
              )}
            </div>
          </div>

          {/* Rate input */}
          <div>
            <label className="block text-gray-400 text-xs font-medium mb-2">건당 발송 가격</label>
            <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 focus-within:border-rose-500/40 transition-colors">
              <span className="text-gray-500 text-sm font-medium">₩</span>
              <input
                type="number"
                min={0}
                step={1}
                value={rateValue}
                onChange={(e) => {
                  setRateValue(e.target.value);
                  setSaved(false);
                  setError(null);
                }}
                placeholder="미설정 (기본값 사용)"
                className="flex-1 bg-transparent text-white text-sm font-medium placeholder-gray-600 focus:outline-none"
              />
            </div>
            {error && (
              <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>
                {error}
              </p>
            )}
            {saved && (
              <p className="text-emerald-400 text-xs mt-1.5 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i>
                저장 완료
              </p>
            )}
            <p className="text-gray-600 text-xs mt-2 flex items-center gap-1">
              <i className="ri-information-line"></i>
              비워두면 기본 요금표가 적용됩니다.
            </p>
          </div>

          {/* Confirm summary */}
          <div className="bg-rose-500/5 border border-rose-500/10 rounded-xl p-3">
            <div className="flex items-center gap-2">
              <i className="ri-alert-line text-rose-400 text-sm"></i>
              <p className="text-gray-400 text-xs">
                <span className="text-white font-medium">{selectedUsers.length.toLocaleString()}명</span>의 회원에게
                <span className="text-white font-medium">
                  {" "}
                  {rateValue === "" ? "기본 요금표" : `₩${Number(rateValue).toLocaleString()}`}
                </span>
                {"가 적용됩니다."}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/[0.08] shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-300 hover:text-white text-sm font-medium cursor-pointer transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
          >
            {saving ? (
              <span className="flex items-center gap-1.5">
                <i className="ri-loader-4-line animate-spin"></i>
                저장 중
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line"></i>
                적용
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}